import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { hasIndividualAccess } from '../../../utils/userRoles';
import { useRoleContext } from '../../../context/RoleContext';
import { CONTEXT_TYPES } from '../../../constants/roleContext';
import { fetchTournamentMatches } from '../../../services/tournamentService';
import PageShell from '../../../components/layout/PageShell';
import { getMatchStartResumePath, getStartResumeLabel } from '../../../utils/matchStatus';
import { normalizeId, resolveMatchManagerId } from '../../../utils/ids';
import { buildMatchManagerMatchesPath, buildMatchManagerMatchTossPath } from '../../../utils/matchManagerPaths';

const formatStatusLabel = (status) => {
  if (!status) return 'Scheduled';
  return status
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatMatchTiming = (match) => {
  if (!match) return 'Schedule TBD';

  let dateLabel = '';
  if (match.date) {
    try {
      dateLabel = new Date(match.date).toLocaleDateString();
    } catch (error) {
      dateLabel = '';
    }
  }

  const timeLabel = match.time || '';

  if (dateLabel && timeLabel) {
    return `${dateLabel} • ${timeLabel}`;
  }

  if (dateLabel) {
    return dateLabel;
  }

  if (timeLabel) {
    return timeLabel;
  }

  if (match.dayLabel) {
    return match.dayLabel;
  }

  if (match.day) {
    return `Day ${match.day}`;
  }

  return 'Schedule TBD';
};

const normalizeName = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const IndividualTournamentMatchesPage = () => {
  const { tournamentId } = useParams();
  const { token, user } = useAuth();
  const { availableContexts } = useRoleContext();
  const [tournament, setTournament] = useState(null);
  const [matchSchedule, setMatchSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const userRoles = useMemo(
    () => new Set([...(user?.roles || []), user?.role].filter(Boolean)),
    [user]
  );
  const hasIndividualRole = hasIndividualAccess(user)
    || availableContexts.some((context) => context.type === CONTEXT_TYPES.INDIVIDUAL);

  useEffect(() => {
    const loadTournamentMatches = async () => {
      if (!token || !hasIndividualRole || !tournamentId) return;

      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchTournamentMatches(tournamentId, token);
        setTournament(response.tournament || null);
        setMatchSchedule(response.matchSchedule || null);
      } catch (error) {
        setLoadError(error.message || 'Unable to load tournament matches.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTournamentMatches();
  }, [hasIndividualRole, token, tournamentId]);

  const matches = useMemo(() => {
    const list = matchSchedule?.matches ? [...matchSchedule.matches] : [];
    return list.sort((a, b) => {
      const dayDiff = Number(a?.day || 0) - Number(b?.day || 0);
      if (dayDiff !== 0) return dayDiff;
      return Number(a?.matchNumber || 0) - Number(b?.matchNumber || 0);
    });
  }, [matchSchedule]);
  const userTeamRoles = useMemo(() => new Set([...(user?.teamRoles || [])].filter(Boolean)), [user]);
  const isScorerRole =
    userRoles.has('scorer') || userTeamRoles.has('scorer') || hasIndividualRole || user?.accountType === 'individual';
  const scorerAliases = useMemo(() => {
    const aliases = new Set();
    [user?.name, user?.email, user?.playerProfile?.fullName].forEach((value) => {
      const normalized = normalizeName(value);
      if (normalized) {
        aliases.add(normalized);
      }
    });
    return aliases;
  }, [user?.email, user?.name, user?.playerProfile?.fullName]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!hasIndividualRole) {
    return <Navigate to="/dashboard/individual/home" replace />;
  }

  return (
    <div className="py-4 d-grid gap-3">
      <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2">
        <div>
          <p className="eyebrow text-primary mb-1">Individual account</p>
          <h1 className="h4 mb-2">{tournament?.name || 'Tournament matches'}</h1>
          <p className="text-muted mb-0">
            Browse every scheduled match and jump into a live scoreboard when available.
          </p>
        </div>
        <div>
          <Link to="/dashboard/individual/tournaments" className="btn btn-outline-secondary">
            Back to tournaments
          </Link>
        </div>
      </div>

      {loadError && <div className="alert alert-danger mb-0">{loadError}</div>}
      {isLoading && <div className="alert alert-secondary mb-0">Loading matches...</div>}

      {!isLoading && matches.length === 0 && (
        <div className="alert alert-info mb-0">No matches have been scheduled yet.</div>
      )}

      {!isLoading &&
        matches.map((match, index) => {
          const statusLabel = match?.score?.matchComplete
            ? 'Completed'
            : formatStatusLabel(match?.score?.status);
          const isMatchFinished =
            Boolean(match?.score?.matchComplete) ||
            (Number(match?.score?.currentInnings) >= 2 &&
              (match?.score?.status === 'completed' || match?.score?.status === 'all_out'));
          const matchNumber = match?.matchNumber || index + 1;
          const matchId = normalizeId(match?.id || match?._id || match?.matchId);
          const scorerName = normalizeName(match?.configuration?.scorer);
          const isAssignedScorer =
            Boolean(scorerName) && isScorerRole && scorerAliases.size > 0 && scorerAliases.has(scorerName);

          const startResumeLabel = getStartResumeLabel(match);
          const resolvedMatchManagerId = resolveMatchManagerId({ match, tournament, currentUser: user });
          const individualMatchBasePath = buildMatchManagerMatchesPath({
            individualTournamentId: tournamentId,
            managerId: resolvedMatchManagerId,
            tournamentId,
          });
          const startResumePath = getMatchStartResumePath({
            match: { ...match, id: matchId },
            matchBasePath: individualMatchBasePath,
            tossPath: buildMatchManagerMatchTossPath({
              individualTournamentId: tournamentId,
              managerId: resolvedMatchManagerId,
              tournamentId,
              matchId,
            }),
          });
          const liveConfigPath = matchId && individualMatchBasePath
            ? `${individualMatchBasePath}/${matchId}/configure`
            : '';

          return (
    <PageShell containerClassName="p-0">
            <div key={match.id || matchNumber} className="card border-0 shadow-sm">
              <div className="card-body d-flex flex-column flex-lg-row justify-content-lg-between gap-3">
                <div>
                  <p className="text-muted small mb-1">Match {matchNumber}</p>
                  <h2 className="h6 mb-1">
                    {match?.team1 || 'Team 1'} vs {match?.team2 || 'Team 2'}
                  </h2>
                  <p className="text-muted small mb-0">{formatMatchTiming(match)}</p>
                </div>
                <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                  <span
                    className={`badge ${
                      statusLabel === 'Completed'
                        ? 'bg-success-subtle text-success-emphasis'
                        : 'bg-secondary-subtle text-secondary-emphasis'
                    }`}
                  >
                    {statusLabel}
                  </span>
                  {liveConfigPath && (
                    <Link to={liveConfigPath} className="btn btn-sm btn-outline-danger">
                      Go Live
                    </Link>
                  )}
                  {matchId && !isMatchFinished && isAssignedScorer && startResumePath && (
                    <Link
                      to={startResumePath}
                      className="btn btn-sm btn-outline-primary"
                    >
                      {startResumeLabel}
                    </Link>
                  )}
                  {match?.publicViewLink && (
                    <Link to={match.publicViewLink} className="btn btn-sm btn-outline-primary">
                      View match
                    </Link>
                  )}
                </div>
              </div>
            </div>
          
    </PageShell>
  );
        })}
    </div>
  );
};

export default IndividualTournamentMatchesPage;
